import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Heart, Eye, Target, Users, Shield, Lightbulb, 
  ChevronRight, Quote, Sparkles, ArrowRight, Zap
} from 'lucide-react';

const FOUNDER_PHOTO = {
  url: "https://customer-assets.emergentagent.com/job_7b2575e8-2794-4e8b-bc83-0e95eaa81cc0/artifacts/kee7oz0g_photo%20for%20about%20us-%20founder%202.PNG",
  alt: "Joyce Cummings - Founder Portrait"
};

const coreValues = [
  {
    icon: Heart,
    title: 'People First',
    description: 'Every resource is created to serve individuals not companies.',
    color: 'purple'
  },
  {
    icon: Eye,
    title: 'Accessibility',
    description: 'Clear guidance without judgment or gatekeeping.',
    color: 'teal'
  },
  {
    icon: Lightbulb,
    title: 'Empowerment',
    description: 'Knowledge that helps people advocate for themselves.',
    color: 'purple'
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'Honest, transparent guidance with clear boundaries.',
    color: 'teal'
  },
  {
    icon: Users,
    title: 'Community',
    description: 'No one should navigate growth, transition, or uncertainty alone.',
    color: 'purple'
  },
];

// Single Photo Component with decorative elements
const FounderPhoto = () => {
  return (
    <div className="relative">
      {/* Main Image Display */}
      <div className="relative w-full max-w-md mx-auto">
        {/* Decorative gradient frames */}
        <div className="absolute -inset-3 bg-gradient-to-br from-hr-purple via-hr-teal to-hr-purple rounded-3xl opacity-30 blur-lg animate-pulse-slow" />
        <div className="absolute -inset-2 bg-gradient-to-tr from-hr-teal to-hr-purple rounded-3xl opacity-20 animate-spin-very-slow" style={{ animationDuration: '20s' }} />
        
        <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl" style={{ aspectRatio: '3/4' }}>
          <img 
            src={FOUNDER_PHOTO.url} 
            alt={FOUNDER_PHOTO.alt}
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          
          {/* Gradient overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      </div>
    </div>
  );
};

export const About = () => {
  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-purple-light/30 relative" data-testid="about-hero">
        {/* Animated background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-hr-teal/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6 animate-bounce-subtle">
              <Sparkles className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Our Story</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              About <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">HR Nest</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Where people come first. We're on a mission to help individuals reset, rebuild, and rise with confidence and dignity.
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-white relative" data-testid="about-vision-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="group border-0 shadow-xl bg-gradient-to-br from-hr-purple via-hr-purple to-hr-purple-dark text-white overflow-hidden hover:-translate-y-3 transition-all duration-500 card-3d animate-fade-in-up" style={{ opacity: 0 }}>
              <CardContent className="p-10 lg:p-12 relative">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-hr-teal/20 rounded-full blur-2xl animate-float" />
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <Eye className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="font-heading text-3xl font-semibold mb-6">
                    Our Vision
                  </h2>
                  <p className="text-white/90 text-lg leading-relaxed">
                    People-first platform that helps individuals especially the middle class and underserved, close opportunity gaps and create long-term stability through informed career and life decisions.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="group border-0 shadow-xl bg-gradient-to-br from-hr-teal via-hr-teal to-hr-teal-dark text-white overflow-hidden hover:-translate-y-3 transition-all duration-500 card-3d animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
              <CardContent className="p-10 lg:p-12 relative">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-hr-purple/20 rounded-full blur-2xl animate-float-slow" />
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center mb-8 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500">
                    <Target className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="font-heading text-3xl font-semibold mb-6">
                    Our Mission
                  </h2>
                  <p className="text-white/90 text-lg leading-relaxed">
                    To provide practical, human-centered guidance, tools, and community support that help individuals navigate career transitions, professional growth, and personal development with clarity and confidence.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg to-white relative overflow-hidden" data-testid="about-values">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-4">
              <Zap className="h-4 w-4 text-hr-teal animate-pulse" />
              <span className="text-hr-teal text-sm font-medium">What We Stand For</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary">
              Our Core <span className="text-hr-teal">Values</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {coreValues.map((value, index) => (
              <Card 
                key={index} 
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 text-center overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-8 relative">
                  {/* Animated hover effect */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 ${
                    value.color === 'purple' ? 'bg-gradient-to-br from-hr-purple/10 to-transparent' : 'bg-gradient-to-br from-hr-teal/10 to-transparent'
                  }`} />
                  
                  <div className={`relative w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 shadow-lg ${
                    value.color === 'purple' 
                      ? 'bg-gradient-to-br from-hr-purple-light to-hr-purple/20 group-hover:from-hr-purple group-hover:to-hr-purple-dark' 
                      : 'bg-gradient-to-br from-hr-teal-light to-hr-teal/20 group-hover:from-hr-teal group-hover:to-hr-teal-dark'
                  }`}>
                    <value.icon className={`h-8 w-8 transition-all duration-500 ${
                      value.color === 'purple' 
                        ? 'text-hr-purple group-hover:text-white' 
                        : 'text-hr-teal group-hover:text-white'
                    }`} />
                  </div>
                  <h3 className="relative font-heading text-lg font-semibold text-hr-text-primary mb-2 group-hover:text-hr-purple transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="relative text-sm text-hr-text-muted">
                    {value.description}
                  </p>
                  
                  {/* Bottom accent */}
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-0 group-hover:w-1/2 transition-all duration-500 rounded-full ${
                    value.color === 'purple' ? 'bg-hr-purple' : 'bg-hr-teal'
                  }`} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="about-founder">
        <div className="absolute top-20 right-20 w-40 h-40 bg-hr-purple/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 left-20 w-60 h-60 bg-hr-teal/5 rounded-full blur-3xl animate-float-slow" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-4 animate-bounce-subtle">
              <Heart className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">Meet Our Founder</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-hr-text-primary">
              Joyce <span className="text-hr-purple">Cummings</span>
            </h2>
            <p className="mt-4 text-lg text-hr-text-secondary">
              Founder of HR Nest
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Single Photo */}
            <div className="lg:sticky lg:top-24">
              <FounderPhoto />
            </div>

            {/* Story Content */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-8">
                <Quote className="h-12 w-12 text-hr-purple animate-pulse-subtle" />
              </div>
              
              <p className="text-lg text-hr-text-secondary leading-relaxed animate-fade-in-up" style={{ opacity: 0 }}>
                I am the founder of HR Nest, a platform designed to support individuals navigating career transitions, personal challenges, and seasons of rebuilding.
              </p>
              
              <p className="text-lg text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
                With over a decade of experience in Human Resources and Business Operations across both the private and public sectors, spanning multiple industries, I bring a rare blend of strategic expertise and lived experience to my work. I earned my bachelor's degree from Indiana University–Purdue University and my MBA from Colorado State University Global (CSU Global).
              </p>
              
              <p className="text-lg text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
                Having seen firsthand how workplace systems can either empower individuals or unintentionally leave them behind, I am deeply passionate about helping others close those gaps and move forward with clarity, dignity, and confidence.
              </p>
              
              <p className="text-lg text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
                My work is deeply personal. It is a continuation of my late father's legacy of advocacy, service, and giving back to the community. His example shaped my belief that success is measured not only by titles, but by impact.
              </p>
              
              <p className="text-lg text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-4" style={{ opacity: 0 }}>
                HR Nest exists to support individuals such as single parents, students, refugees, immigrants, high school graduates, college graduates, career changers, and individuals navigating life and work transitions by providing practical tools, guidance, and encouragement to move forward with clarity and confidence.
              </p>
              
              <div className="p-6 bg-gradient-to-r from-hr-purple-light to-hr-teal-light rounded-2xl animate-fade-in-up stagger-5" style={{ opacity: 0 }}>
                <p className="text-xl font-semibold text-hr-purple">
                  I believe people deserve access, understanding, and opportunity and I am committed to building pathways that help them thrive and achieve their greatest!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-hr-purple via-hr-purple-dark to-hr-purple relative overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl animate-float" />
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-blob" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6 animate-fade-in-up">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/80 text-lg mb-12 max-w-2xl mx-auto animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
            Join our community and take the first step toward your goals. We're here to support you every step of the way.
          </p>
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up stagger-2" style={{ opacity: 0 }}>
            <Link to="/services" data-testid="about-explore-services-btn">
              <Button className="group bg-white text-hr-purple hover:bg-stone-100 rounded-full px-10 py-7 text-lg font-medium shadow-2xl hover:shadow-white/30 transition-all duration-500 hover:-translate-y-3 hover:scale-105">
                Explore Services
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </Link>
            <Link to="/contact" data-testid="about-contact-btn">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-10 py-7 text-lg font-medium transition-all duration-500 hover:-translate-y-3 hover:scale-105">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
